import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { useIntl } from "react-intl";
import { FormikProps, useFormik } from "formik";
import { DefaultModal, FormInput, Forms, FormSelect, GradientButton, notify, TFormSelectOption } from "@housebets/housebets-x-package";

import apiWallet from "app/api/wallet";
import {useAppSelector} from "hooks/useAppSelector";
import { useWindowSize } from "app/utils/hooks/useWindowSize";
import CopyIcon from "app/assets/svg/copy.svg?react";
import { copyToBuffer } from "app/utils/copy-to-buffer";

import { CurrencyConfig } from "app/components/features/wallet";
import { useGTMEvents } from "app/hooks/useGTMEvents";

import { IDepositCashFormProps, TFormikConfig, TPayload } from "./types";
import { capitalizeWords } from "app/utils/text";
import pkg from 'lodash';
const {sortBy} = pkg;

export const DepositCashForm: React.FC<IDepositCashFormProps> = ({
    category,
}) => {
    const { $t } = useIntl();

    const { isMobile } = useWindowSize();
    const { form, deposit } = useGTMEvents();
    const payProviderList = useAppSelector((state) => state.config.platformConfig?.payProviderList) || [];

    const [showNgpay, setShowNgpay] = useState(false);
    const [ngpayData, setNgpayData] = useState({
        accountName: "",
        accountNumber: "",
        amount: "",
        bankName: "",
        validityPeriodMins: "",
    });

    const depositCashConfig = useMemo(() => {
        return CurrencyConfig.find((c) => c.type === category?.id)!.deposit;
    }, [category]);

    const channelList: TFormSelectOption[] = useMemo(() => {
        const channels = payProviderList.filter((p) => p.tokenId === category?.id && p.canDeposit === 1) || [];
        const sortedArr = sortBy(channels, (item) => item.listOrder);
        return sortedArr.map((v) => {
            return {
                id: v.providerName || "",
                name: capitalizeWords(v.providerName) || "",
            };
        });
    }, [category, payProviderList]);

    const generateInitialValues = (channelCode: string): TFormikConfig => {
        const fields = depositCashConfig[channelCode] || [];
        const initialValues: TFormikConfig = {
            channelCode: channelCode,
            amount: "",
        };

        fields.forEach((field) => {
            initialValues[field.name] = "";
        });

        return initialValues;
    };

    const initData: TFormikConfig = {
        channelCode: channelList[0].id,
        amount: "",
    };

    const formSchema = (channelCode: string) => {
        const fields = depositCashConfig[channelCode] || [];
        const shape: { [key: string]: any } = {
            channelCode: Yup.string().required($t({ id: "FieldIsRequired" })),
            amount: Yup.string().required($t({ id: "FieldIsRequired" })),
        };

        fields.forEach((field) => {
            if (field.type === "select" || field.type === "input" || field.type === "phone") {
                shape[field.name] = Yup.string().required($t({ id: "FieldIsRequired" }));
            }
        });

        return Yup.object().shape(shape);
    };

    const formik: FormikProps<TFormikConfig> = useFormik<TFormikConfig>({
        initialValues: generateInitialValues(initData.channelCode + ""),
        validationSchema: formSchema(initData.channelCode + ""),
        validateOnBlur: true,
        onSubmit: async (_data) => {
            const payload: TPayload = {
                ..._data,
                amount: Number(_data.amount),
                currency: category?.title || "",
            };

            deposit.init({
                amount: Number(_data.amount),
                currency: category?.title || "",
            });

            const { status, data } = await apiWallet.depositFiat(payload);
            if (status === 200 && data.code === 0) {
                form.event({
                    event: 'form_submit',
                    form_id: 'deposit_cash_form',
                    form_name: 'Deposit Cash Form',
                });

                deposit.success({
                    amount: Number(_data.amount),
                    currency: category?.title || "",
                    payment_method: 'credit_card'
                });

                if (payload.channelCode === "ngpay") {
                    setNgpayData(JSON.parse(data.data));
                    setShowNgpay(true);
                    return;
                }

                if (isMobile) {
                    window.location.href = data.data;
                    return;
                }
                window.open(data.data);
                return;
            }
            notify({
                type: "error",
                title: `Deposit error ${data.code}`,
            });
            deposit.error({
                amount: Number(_data.amount),
                currency: category?.title || "",
                payment_method: 'credit_card',
                message: `Deposit error ${data.code}`,
            });
        },
    });

    useEffect(() => {
        const newInitialValues = generateInitialValues(formik.values.channelCode + "");

        formik.setValues(newInitialValues);
        formik.setFormikState((prev) => ({
            ...prev,
            validationSchema: formSchema(formik.values.channelCode + ""),
        }));
    }, [category, formik.values.channelCode]);

    useEffect(() => {
        formik.setErrors({});
        formik.setTouched({});
        formik.setFieldValue("channelCode", channelList[0].id);
    }, [category]);

    useEffect(() => {
        const keys = Object.keys(formik.errors);
        keys.forEach((item) => {
            form.error({
                field: item,
                message: formik.errors[item as keyof TFormikConfig] as string,
                form_id: 'deposit_cash_form',
                form_name: 'Deposit Cash Form',
            });
        });
    }, [formik.errors]);

    const renderDynamicFields = () => {
        const fields = depositCashConfig[formik.values?.channelCode] || [];
        return fields.map((field) => {
            const fieldName = field.name;

            if (field.ifShow) {
                const { field: showField, value } = field.ifShow;
                if (formik.values[showField] !== value) {
                    return null;
                }
            }

            if (field.type === "select") {
                const selectOptions =
                    field.options?.map((v) => {
                        return {
                            id: v.value,
                            name: v.label,
                        };
                    }) || [];
                return (
                    <Forms.WalletForm.FormRow key={field.name}>
                        <FormSelect
                            size="sm"
                            options={selectOptions}
                            placeholder={$t({ id: field.label })}
                            label={$t({ id: field.label })}
                            name={field.name}
                            value={formik.values[fieldName] + ""}
                            errors={!!formik.errors[fieldName] && !!formik.touched[fieldName] ? { message: $t({ id: "FieldIsRequired" }) } : null}
                            onChange={(val) => {
                                formik.setFieldValue(fieldName, val[0]);
                            }}
                            onFocus={() => formik.setFieldTouched(fieldName, false)}
                            onBlur={() => formik.setFieldTouched(fieldName, true)}
                        />
                    </Forms.WalletForm.FormRow>
                );
            }

            return (
                <Forms.WalletForm.FormRow key={field.name}>
                    <FormInput
                        size="sm"
                        placeholder={$t({ id: field.label })}
                        name={field.name}
                        type={field.type === "phone" ? "number" : field.type}
                        errorMsgAbsolute
                        label={$t({ id: field.label })}
                        value={formik.values[fieldName] || ""}
                        onChange={(e) => {
                            formik.setFieldValue(fieldName, e);
                        }}
                        onBlur={() => formik.setFieldTouched(fieldName, true)}
                        errors={!!formik.errors[fieldName] && !!formik.touched[fieldName] ? { message: $t({ id: "FieldIsRequired" }) } : null}
                    />
                </Forms.WalletForm.FormRow>
            );
        });
    };

    return (
        <Forms.WalletForm.Wrapper title={$t({ id: "DepositCash" })}>
            <form
                id="deposit_cash_form"
                onSubmit={formik.handleSubmit}
            >
                <Forms.WalletForm.FormRow>
                    <FormSelect
                        size="sm"
                        options={channelList}
                        placeholder={$t({ id: "PaymentProvider" })}
                        label={$t({ id: "PaymentProvider" })}
                        name="channelCode"
                        value={formik.values?.channelCode as string}
                        errors={
                            !!formik.errors?.channelCode && !!formik.touched?.channelCode
                                ? {
                                    message: formik.errors?.channelCode,
                                }
                                : null
                        }
                        onChange={(val) => formik.setFieldValue("channelCode", val[0])}
                        onFocus={() => {
                            formik.setFieldTouched("channelCode", false);
                        }}
                        onBlur={() => {
                            formik.setFieldTouched("channelCode", true);
                        }}
                    />
                </Forms.WalletForm.FormRow>

                <Forms.WalletForm.FormRow>
                    <FormInput
                        size="sm"
                        placeholder={$t({ id: "Amount" })}
                        name="amount"
                        type="text"
                        errorMsgAbsolute
                        label={$t({ id: "DepositAmount" })}
                        value={formik.values?.amount}
                        errors={
                            !!formik.errors?.amount && !!formik.touched?.amount
                                ? {
                                    message: formik.errors?.amount,
                                }
                                : null
                        }
                        onChange={(val) => formik.setFieldValue("amount", val)}
                        onFocus={() => formik.setFieldTouched("amount", false)}
                        onBlur={() => formik.setFieldTouched("amount", true)}
                    />
                </Forms.WalletForm.FormRow>

                {renderDynamicFields()}

                <Forms.WalletForm.BottomGroup>
                    <Forms.WalletForm.FormRow>
                        <GradientButton
                            id="deposit_cash_form_submit_button"
                            fullWidth
                            type="submit"
                            kind="fill"
                            text={$t({ id: "ConfirmDeposit" })}
                            disabled={!formik.isValid}
                            style={{
                                textTransform: 'unset',
                            }}
                        />
                    </Forms.WalletForm.FormRow>
                </Forms.WalletForm.BottomGroup>
            </form>

            <DefaultModal
                title="123"
                isOpen={showNgpay}
                onClose={() => {
                    setShowNgpay(false);
                }}>
                <div
                    style={{
                        color: "white",
                        fontSize: "16px",
                        fontWeight: 700
                    }}
                >
                    {$t({ id: "PayWithBankTransfer" })}
                </div>

                <div
                    style={{
                        marginTop: 16,
                        backgroundColor: "#002026",
                        borderRadius: 5,
                        padding: 12,
                        color: "#FFD205",
                        lineHeight: 1.5
                    }}
                >
                    {$t({ id: "TransferAccountNotice" })}
                </div>

                <div>
                    <div
                        style={{
                            marginTop: 16,
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            color: "white",
                            fontSize: 16,
                            fontWeight: 500,
                        }}>
                        <div
                            style={{
                                color: "#98A7B5"
                            }}
                        >
                            {$t({ id: "BankName" })}
                        </div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8
                            }}
                        >
                            <div>{ngpayData.bankName}</div>
                            <CopyIcon
                                style={{ width: 20, height: 20, cursor: "pointer" }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    copyToBuffer(ngpayData.bankName);
                                    notify({
                                        type: "success",
                                        title: "Copied",
                                    });
                                }}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            marginTop: 16,
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            color: "white",
                            fontSize: 16,
                            fontWeight: 500,
                        }}>
                        <div style={{ color: "#98A7B5" }}>{$t({ id: "AccountNumber" })}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div>{ngpayData.accountNumber}</div>
                            <CopyIcon
                                style={{ width: 20, height: 20, cursor: "pointer" }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    copyToBuffer(ngpayData.accountNumber);
                                    notify({
                                        type: "success",
                                        title: "Copied",
                                    });
                                }}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            marginTop: 16,
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            color: "white",
                            fontSize: 16,
                            fontWeight: 500,
                        }}>
                        <div style={{ color: "#98A7B5" }}>{$t({ id: "BeneficiaryName" })}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div>{ngpayData.accountName}</div>
                            <CopyIcon
                                style={{ width: 20, height: 20, cursor: "pointer" }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    copyToBuffer(ngpayData.accountName);
                                    notify({
                                        type: "success",
                                        title: "Copied",
                                    });
                                }}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            marginTop: 16,
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            color: "white",
                            fontSize: 16,
                            fontWeight: 500,
                        }}>
                        <div style={{ color: "#98A7B5" }}>{$t({ id: "Amount" })}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div>{ngpayData.amount}</div>
                            <CopyIcon
                                style={{ width: 20, height: 20, cursor: "pointer" }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    copyToBuffer(ngpayData.amount);
                                    notify({
                                        type: "success",
                                        title: "Copied",
                                    });
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        width: 360,
                        height: 48,
                        margin: "20px auto 0",
                        background:
                            "linear-gradient(270deg, rgba(255, 255, 255, 0.6) -19.29%, rgba(255, 255, 255, 0) 40.37%),linear-gradient(141deg, #0ff 32.81%, rgba(0, 255, 255, 0) 82.1%),linear-gradient(180deg, #0ff 47.75%, #05c5c5 50.46%),linear-gradient(0deg, var(--Primary-Accent, #0ff) 0%, var(--Primary-Accent, #0ff) 100%),#020410",
                        borderRadius: 5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#1a1d28",
                        fontSize: 16,
                        fontWeight: 600,
                        cursor: "pointer",
                    }}
                    onClick={() => {
                        setShowNgpay(false);
                    }}>
                    {$t({ id: "IHaveTransferred" })}
                </div>
            </DefaultModal>
        </Forms.WalletForm.Wrapper>
    )
}

DepositCashForm.displayName = 'DepositCashForm';